import * as React from "react";

import { Global, css } from "@emotion/react";
import { breakpoints } from "./breakpoints";
import ArrowDown from "../svgs/arrow-down.svg";
import Tick from "../svgs/tick.svg";

export const GlobalStyles = () => (
  <Global
    styles={css`
      /* ------------------------------------------------ */
      /* ----- Basic Setup ----- */
      /* ------------------------------------------------ */

      :root {
        --green: #3ac95d;
        --black: #000000;
        --dark-grey: #151515;
        --grey: #99999990;
        --white: #ffffff;
        --off-white: #fafafa;
        --off-white-1: #f2f2f2;
        --off-white-2: #f3f3f3;
        --divider: #dddddd;
        --divider-light: #f0f0f0;

        --row-max-w: 1346px;
        --row-lg-max-w: 1070px;
        --row-md-max-w: 760px;
        --row-sm-max-w: 350px;

        --header-height: 105px;
        --header-height-tablet: 80px;
        --header-height-mobile: 65px;

        --announcement-height-tablet: 40px;
        --announcement-height-mobile: 64px;

        --x-large: 13rem;
        --large: 8rem;
        --medium: 6rem;
        --small: 5rem;
        --x-small: 4rem;
        --xx-small: 3rem;
        --mini: 2.5rem;
        --macro: 2rem;
        --mimac: 1.5rem;
        --micro: 1rem;
        --nano: 0.5rem;

        --h1: 5.4rem;
        --h2: 4.8rem;
        --p-large: 2.4rem;
        --p-normal: 2rem;
        --p-small: 1.8rem;
        --p-x-small: 1.6rem;
        --p-xx-small: 1.4rem;

        --blob-h2: 3rem;
        --blob-h3: 2.4rem;

        @media (max-width: ${breakpoints.xs}) {
          --h1: 4.6rem;
          --h2: 4rem;
          --p-large: 2.2rem;
          --p-normal: 1.9rem;
          --p-small: 1.8rem;
        }

        --shadow-normal: 0 0.3rem 0.5rem 0 rgba(0, 0, 0, 0.1);
        --shadow-large: 0 2rem 4rem 0 rgba(0, 0, 0, 0.3);
      }

      *,
      *::before,
      *::after {
        margin: 0;
        padding: 0;
        box-sizing: inherit;
      }

      html {
        box-sizing: border-box;
        font-size: 62.5%;
        scroll-padding: var(--medium);

        @media (max-width: ${breakpoints.lg}) {
          font-size: 50%;
        }
      }

      body {
        font-family: "Inter", sans-serif;
        font-size: var(--p-small);
        color: var(--dark-grey);
        font-weight: 400;
        line-height: 167%;
        overflow-x: hidden;
        max-width: 100vw;
      }

      /* ------------------------------------------------ */
      /* ----- Reusable Components ----- */
      /* ------------------------------------------------ */

      .row {
        max-width: var(--row-max-w);
        width: 100%;
        margin: 0 auto;

        @media (max-width: ${breakpoints["2xl"]}) {
          padding: 0 var(--small);
        }

        @media (max-width: ${breakpoints.md}) {
          padding: 0 var(--x-small);
        }

        @media (max-width: ${breakpoints.xs}) {
          padding: 0 var(--macro);
        }
      }

      .container-medium {
        max-width: 107rem;
        margin: auto;
      }

      .pattern-bg {
        position: relative;

        &::before {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          height: 100%;
          width: 100%;
          background-color: var(--off-white-2);
          clip-path: polygon(100% 0, 0 100%, 100% 100%);
          z-index: -1;
        }
      }

      /* ------------------------------------------------ */
      /* ----- Headlines & Paragraphs ----- */
      /* ------------------------------------------------ */

      h1,
      h2,
      h3,
      .h3,
      h4 {
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--black);
      }

      .h1 {
        font-size: var(--h1);
        line-height: 124%;
      }

      .h2 {
        font-size: var(--h2);
        line-height: 115%;
      }

      .h3 {
        font-size: var(--p-large);
        line-height: 133%;
      }

      .text-large {
        font-size: var(--p-large);
        line-height: 142%;
      }

      /* ------------------------------------------------ */
      /* ----- Links, Buttons and Form Elements ----- */
      /* ------------------------------------------------ */

      a {
        text-decoration: none;
      }

      a,
      button {
        font-weight: 600;
        cursor: pointer;
        color: inherit;
      }

      button {
        border: none;
        background: none;
      }

      [class|="btn"],
      .btn {
        position: relative;
        display: inline-block;
        max-width: 100%;
        text-align: center;
        border: 0.2rem solid;
        border-radius: 0.7rem;
        padding: 1.2rem 3rem;
        white-space: nowrap;
        transition: all 0.6s cubic-bezier(0.76, 0, 0.24, 1);
        z-index: 1;
        overflow: hidden;

        &:hover,
        &:focus {
          &::before {
            right: 0;
          }
        }

        &::before,
        &::after {
          content: "";
          display: block;
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
        }

        &::before {
          right: 100%;
          z-index: -1;
          transition: all 0.6s cubic-bezier(0.76, 0, 0.24, 1);
        }

        &::after {
          right: 0;
          z-index: -2;
        }
      }

      .btn-brand {
        color: var(--white);
        border-color: var(--green);
        text-transform: uppercase;

        &::after {
          background-color: var(--green);
        }

        &:hover,
        &:focus {
          color: var(--green);
          &::before {
            background: var(--white);
          }
        }
      }

      .btn-dark {
        display: inline-flex;
        align-items: center;
        color: var(--white);
        border-color: var(--black);

        &::before,
        &::after {
          background: var(--black);
        }

        &:hover,
        &:focus {
          color: var(--black);
          &::before {
            background: var(--white);
          }
        }
      }

      .btn-ghost {
        color: var(--green);

        &::before,
        &::after {
          background: var(--white);
        }

        &:hover,
        &:focus {
          color: var(--white);
          border-color: var(--green);

          &::before {
            background: var(--green);
          }
        }
      }

      input,
      textarea,
      select {
        border: 3px solid var(--off-white-1);
        border-radius: 0.7rem;
      }

      input,
      textarea,
      button,
      select {
        font-family: inherit;
        font-size: inherit;
        transition: all 0.2s;
      }

      select {
        background: transparent;
        appearance: none;
        background-image: url(${ArrowDown});
        background-position: right 1em top 50%, 0 0;
        background-repeat: no-repeat, repeat;
        background-size: 1.25em auto, 100%;
        width: 100%;
        font-weight: 700;
      }

      option {
        font-weight: 400;
      }

      input,
      textarea {
        &:focus {
          outline: none;
          border: 3px solid var(--green);
        }
      }

      .form-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        max-width: 1180px;
        margin: var(--x-small) auto 0;
        padding: var(--small);
        background: var(--white);
        border-radius: 0.7rem;
        box-shadow: var(--shadow-normal);

        @media (max-width: 1260px) {
          max-width: 700px;
          flex-direction: column;
        }

        @media (max-width: ${breakpoints.sm}) {
          padding: var(--small) var(--macro);
        }

        & > * {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
      }

      .input-half {
        flex: 45%;
        max-width: 520px;

        @media (max-width: 1260px) {
          max-width: 100%;
        }
      }

      [class*="input"] {
        > label,
        p {
          margin-bottom: var(--micro);
        }
      }

      input:not([type="checkbox"]),
      select,
      textarea,
      .checkboxes {
        padding: var(--mimac);
        margin-bottom: var(--xx-small);
      }

      .checkbox {
        margin-top: var(--mimac);
        label {
          position: relative;
          display: flex;
          align-items: center;
        }

        label::before {
          content: "";
          display: inline-block;
          height: 2.4rem;
          width: 2.4rem;
          margin-right: var(--micro);
          background: var(--white);
          border-radius: 0.4rem;
        }

        label::after {
          content: url(${Tick});
          position: absolute;
          left: 0.2rem;
          height: 2rem;
          width: 2rem;
          opacity: 0;
          transform: scale(0.5);
          transition: all 0.2s;
        }

        [type="checkbox"]:checked + label::after {
          opacity: 1;
          transform: scale(1);
        }

        &:not(:last-child) {
          margin-right: var(--xx-small);

          @media (max-width: ${breakpoints.sm}) {
            margin-right: var(--macro);
          }
        }
      }

      /* ------------------------------------------------ */
      /* ----- Images and Objects ----- */
      /* ------------------------------------------------ */

      img,
      video {
        display: block;
        object-fit: contain;
        max-width: 100%;
      }

      /* ------------------------------------------------ */
      /* ----- Lists ----- */
      /* ------------------------------------------------ */

      ul {
        list-style: none;
      }

      /* ------------------------------------------------ */
      /* ----- Utils ----- */
      /* ------------------------------------------------ */

      .text-center {
        text-align: center;
      }

      .visually-hidden {
        position: absolute;
        left: -100000rem;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;
      }

      /* ------------------------------------------------ */
      /* ----- Component Specific ----- */
      /* ------------------------------------------------ */

      body.mobile-menu-is-shown.banner-is-shown {
        .mobile-menu {
          height: calc(
            100vh - var(--header-height-mobile) -
              var(--announcement-height-mobile)
          );
          top: calc(
            var(--header-height-mobile) + var(--announcement-height-mobile)
          );
        }
      }
    `}
  />
);
